import { useEffect, type ReactNode } from 'react';
import { siteHead } from '../../shared/siteSeo';
import { applySiteHead } from './site/siteHead';
import { Link } from 'react-router-dom';
import { Compass } from 'lucide-react';
import { EmptyState } from '../components/States';
import { usePageTitle } from '../components/Page';
import { Wordmark } from '../components/Brand';
import { homePathFor, useAuth } from '../lib/auth';
import { useUiLocale } from '../lib/i18n';

const TEXT = {
  en: {
    pageTitle: 'Not found',
    title: 'This page does not exist',
    workspace: 'Go to your workspace',
    body: 'Check the address, or use the navigation to find what you need.',
  },
  'pt-BR': {
    pageTitle: 'Página não encontrada',
    title: 'Esta página não existe',
    workspace: 'Ir para o painel',
    body: 'Confira o endereço ou use a navegação para encontrar o que precisa.',
  },
};

export function FullPageMessage({ children }: { children: ReactNode }) {
  return (
    <div className="fullpage">
      <div className="fullpage__inner">{children}</div>
    </div>
  );
}

export function NotFound({ inline }: { inline?: boolean }) {
  // Only the inline variant can render inside the pt-BR admin shell.
  const text = TEXT[useUiLocale()];
  usePageTitle(text.pageTitle);
  // The full-page variant is the www.perzo.art not-found page: its head is the server's 404 head (noindex, no
  // canonical), also after navigating here from a public page.
  useEffect(() => {
    if (!inline) applySiteHead(siteHead(null));
  }, [inline]);
  const { me } = useAuth();
  const content = (
    <EmptyState
      title={text.title}
      icon={<Compass size={20} aria-hidden="true" />}
      action={
        me ? (
          <Link className="btn btn--primary" to={homePathFor(me)}>
            {text.workspace}
          </Link>
        ) : (
          <>
            <Link className="btn btn--primary" to="/">
              Go to the Perzo home page
            </Link>
            <Link className="btn btn--ghost" to="/login">
              Sign in
            </Link>
          </>
        )
      }
    >
      {text.body}
    </EmptyState>
  );
  if (inline) return content;
  return (
    <FullPageMessage>
      <div className="stack" style={{ gap: 28 }}>
        <Link to="/" className="standalone__home" aria-label="Perzo home">
          <Wordmark />
        </Link>
        {content}
      </div>
    </FullPageMessage>
  );
}
